import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../axios/api.js";

// ─── Fetch deleted / not-interested companies list ─────────────────────────
export const fetchDeletedCompanies = createAsyncThunk(
  "notInterested/fetchDeletedCompanies",
  async (market, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get("/api/deleted-companies-for-followup", {
        params: { market },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch deleted companies");
    }
  }
);

// ─── Move a company back to active companies (then refresh the list) ──────
export const moveDeletedCompany = createAsyncThunk(
  "notInterested/moveDeletedCompany",
  async ({ companyId, market }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosClient.post(`/api/move-deleted-company/${companyId}`);

      if (!response.data.success) {
        return rejectWithValue(response.data.error || "Failed to move company");
      }

      await dispatch(fetchDeletedCompanies(market));
      return { message: response.data.message || "Company moved successfully" };
    } catch (error) {
      let errorMessage = "Failed to move company";
      let errorDetails = "";

      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = "Company not found";
          errorDetails = error.response.data.details || "";
        } else if (error.response.status === 400) {
          errorMessage = "Invalid data format";
          errorDetails = error.response.data.details || "";
        } else {
          errorMessage = error.response.data?.error || errorMessage;
          errorDetails = error.response.data?.details || "";
        }
      } else if (error.request) {
        errorMessage = "No response from server";
      } else {
        errorMessage = error.message;
      }

      return rejectWithValue(`${errorMessage}${errorDetails ? `: ${errorDetails}` : ""}`);
    }
  }
);

// ─── Contact persons for a company (NotInterestedContactsModal) ───────────
export const fetchAllContactsData = createAsyncThunk(
  "notInterested/fetchAllContactsData",
  async (companyId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(`/api/all-deleted-companydata/${companyId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue("company contacts data Not found");
    }
  }
);

const notInterestedSlice = createSlice({
  name: "notInterested",
  initialState: {
    companies: [],
    fetching: true,
    error: null,

    moving: false,
    movingCompanyId: null,

    allContactsData: [],
    contactsLoading: false,
  },
  reducers: {
    clearAllContactsData: (state) => {
      state.allContactsData = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchDeletedCompanies
      .addCase(fetchDeletedCompanies.pending, (state) => {
        state.fetching = true;
        state.error = null;
      })
      .addCase(fetchDeletedCompanies.fulfilled, (state, action) => {
        state.fetching = false;
        state.companies = action.payload;
      })
      .addCase(fetchDeletedCompanies.rejected, (state, action) => {
        state.fetching = false;
        state.companies = [];
        state.error = action.payload;
      })

      // moveDeletedCompany
      .addCase(moveDeletedCompany.pending, (state, action) => {
        state.moving = true;
        state.movingCompanyId = action.meta.arg.companyId;
        state.error = null;
      })
      .addCase(moveDeletedCompany.fulfilled, (state) => {
        state.moving = false;
        state.movingCompanyId = null;
      })
      .addCase(moveDeletedCompany.rejected, (state, action) => {
        state.moving = false;
        state.movingCompanyId = null;
        state.error = action.payload;
      })

      // fetchAllContactsData
      .addCase(fetchAllContactsData.pending, (state) => {
        state.contactsLoading = true;
      })
      .addCase(fetchAllContactsData.fulfilled, (state, action) => {
        state.contactsLoading = false;
        state.allContactsData = action.payload;
      })
      .addCase(fetchAllContactsData.rejected, (state, action) => {
        state.contactsLoading = false;
        state.allContactsData = [];
        state.error = action.payload;
      });
  },
});

export const { clearAllContactsData } = notInterestedSlice.actions;
export default notInterestedSlice.reducer;
